import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { xeroApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Link2, Link2Off, CheckCircle2, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { XeroSetupHint } from './XeroSetupHint';
import { XeroWebhookHint } from './XeroWebhookHint';

export function XeroPanel() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [codes, setCodes] = useState({ xero_revenue_account_code: '200', xero_bank_account_code: '', xero_tax_type: 'OUTPUT' });
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await xeroApi.status();
      setStatus(data);
      setCodes({ xero_revenue_account_code: data.xero_revenue_account_code, xero_bank_account_code: data.xero_bank_account_code, xero_tax_type: data.xero_tax_type });
      if (data.connected) xeroApi.accounts().then((r) => setAccounts(r.data)).catch(() => setAccounts(null));
    } catch { toast.error('Could not load Xero status'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const result = params.get('xero');
    if (!result) return;
    if (result === 'connected') toast.success('Xero connected', { id: 'xero-result' });
    else toast.error(`Xero connection failed (${params.get('reason') || 'unknown'})`, { id: 'xero-result' });
    const next = new URLSearchParams(params); next.delete('xero'); next.delete('reason'); setParams(next, { replace: true });
  }, [params, setParams]);

  const connect = async () => {
    setBusy('connect');
    try { const { data } = await xeroApi.connect(); window.location.assign(data.authorize_url); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not start Xero connection', { duration: 15000 }); setBusy(''); }
  };
  const disconnect = async () => {
    if (!window.confirm('Disconnect Xero? Invoices will stop syncing until you reconnect.')) return;
    setBusy('disconnect');
    try { await xeroApi.disconnect(); toast.success('Xero disconnected'); setAccounts(null); load(); }
    catch { toast.error('Could not disconnect'); } finally { setBusy(''); }
  };
  const saveCodes = async () => {
    setBusy('codes');
    try { await xeroApi.updateSettings(codes); toast.success('Xero account codes saved'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); } finally { setBusy(''); }
  };

  if (!status) return <div className="animate-pulse h-32 bg-muted rounded-md" />;

  return (
    <Card data-testid="xero-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><Link2 className="h-4 w-4 text-primary" /> Xero accounting</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className={`flex items-start gap-3 p-3 rounded-md border ${status.connected ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`} data-testid="xero-status">
          {status.connected ? <CheckCircle2 className="h-5 w-5 text-green-700 mt-0.5" /> : <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5" />}
          <div className="text-sm flex-1">
            <p className="font-medium" data-testid="xero-status-text">{status.connected ? `Connected to ${status.tenant_name}` : status.configured ? 'Not connected' : 'Xero app credentials missing'}</p>
            <p className="text-xs text-muted-foreground">{status.connected ? 'Invoices are created in Xero when you press Send invoice, and payments are recorded when an invoice is paid.' : 'Connect your Xero organisation so invoices and payments sync automatically.'}</p>
          </div>
          {status.connected
            ? <Button size="sm" variant="outline" onClick={disconnect} disabled={busy === 'disconnect'} data-testid="xero-disconnect-btn"><Link2Off className="h-4 w-4 mr-1" /> Disconnect</Button>
            : <Button size="sm" onClick={connect} disabled={!status.configured || busy === 'connect'} data-testid="xero-connect-btn">{busy === 'connect' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Link2 className="h-4 w-4 mr-1" />} Connect Xero</Button>}
        </div>

        {!status.connected && status.configured && <XeroSetupHint />}
        {status.configured && <XeroWebhookHint status={status} />}

        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label>Sales account code</Label>
            {accounts?.revenue?.length ? (
              <select value={codes.xero_revenue_account_code} onChange={(e) => setCodes((c) => ({ ...c, xero_revenue_account_code: e.target.value }))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="xero-revenue-select">
                {!accounts.revenue.some((a) => a.code === codes.xero_revenue_account_code) && <option value={codes.xero_revenue_account_code}>{codes.xero_revenue_account_code}</option>}
                {accounts.revenue.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
              </select>
            ) : <Input value={codes.xero_revenue_account_code} onChange={(e) => setCodes((c) => ({ ...c, xero_revenue_account_code: e.target.value }))} data-testid="xero-revenue-input" />}
          </div>
          <div className="space-y-1.5">
            <Label>Bank account (payments)</Label>
            {accounts?.bank?.length ? (
              <select value={codes.xero_bank_account_code} onChange={(e) => setCodes((c) => ({ ...c, xero_bank_account_code: e.target.value }))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="xero-bank-select">
                <option value="">First bank account</option>
                {accounts.bank.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
              </select>
            ) : <Input value={codes.xero_bank_account_code} onChange={(e) => setCodes((c) => ({ ...c, xero_bank_account_code: e.target.value }))} placeholder="auto" data-testid="xero-bank-input" />}
          </div>
          <div className="space-y-1.5"><Label>GST tax type</Label><Input value={codes.xero_tax_type} onChange={(e) => setCodes((c) => ({ ...c, xero_tax_type: e.target.value }))} data-testid="xero-tax-input" /></div>
        </div>
        <div className="flex gap-2">
          <Button type="button" size="sm" onClick={saveCodes} disabled={busy === 'codes'} data-testid="xero-save-codes">Save Xero codes</Button>
          {status.connected && <Button type="button" size="sm" variant="ghost" onClick={load} data-testid="xero-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>}
        </div>
      </CardContent>
    </Card>
  );
}
